/* eslint-disable no-nested-ternary */
import { useMutation } from "@apollo/client";
import { RunScreenerInput } from "components/screener/v2/RunScreenerInput";
import StoreRunScreenerInputMutation from "components/screener/v2/mutations/StoreRunScreenerInputMutation";
import type {
  IStoreRunScreenerInputMutation,
  IStoreRunScreenerInputMutationVariables,
} from "components/screener/v2/mutations/__generated__/StoreRunScreenerInputMutation.generated";
import RunScreenerInputQuery from "components/screener/v2/query/RunScreenerInputQuery";
import type {
  IRunScreenerInputQuery,
  IRunScreenerInputQueryVariables,
} from "components/screener/v2/query/__generated__/RunScreenerInputQuery.generated";
import classNames from "components/ui/classNames";
import emptyFunction from "components/utils/emptyFunction";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { FundamentalProductOriginCodes } from "lib/analytics/FundamenteiProductCodes";
import trackEvent from "lib/analytics/trackEvent";
import { BookmarkIcon, CircleAlertIcon, CircleCheckIcon, LoaderCircleIcon } from "lucide-react";
import { customAlphabet } from "nanoid";
import Head from "next/head";
import { startTransition, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import type { IStoreRunScreenerInput } from "types/graphqlTypes";

const nanoScreenerId = customAlphabet("0123456789abcdefghijklmnopqrstuvwxyz", 6);

interface SaveScreenerProps {
  disabled?: boolean;
  objectId?: string | null;
  runScreenerInput?: NonNullable<IRunScreenerInputQuery["runScreenerInput"]> | null;
  onSave?: (result: NonNullable<IStoreRunScreenerInputMutation["storeRunScreenerInput"]>) => void;
}
export function SaveScreener({ disabled, objectId, runScreenerInput, onSave }: SaveScreenerProps) {
  const intl = useIntl();

  const [saveState, setSaveState] = useState<"idle" | "saved" | "error">("idle");
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  useEffect(() => {
    // TODO(Andrey): Memory leak? I don't know if that's a "code smell" or not
    let unsubscribe: (() => void) | null = null;
    const timeoutId = setTimeout(() => {
      unsubscribe = RunScreenerInput.onInputChange(() => {
        if (runScreenerInput !== null) {
          setHasPendingChanges(() => true);
        }
      });
    }, 3000);
    return () => {
      // The problem that happens here is the following: once the user changes the saved screener, it will fire a new
      // `runScreener` request and then it will call `onInputChange` again, which will set the `hasPendingChanges`.
      // However, we only want to capture changes within the same screener...that's why I "delay" the listener
      // installation by 3s
      // setHasPendingChanges(() => false);
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [runScreenerInput?.objectId]);

  useEffect(() => {
    if (saveState !== "idle") {
      const timeoutId = setTimeout(() => setSaveState("idle"), 2000);
      return () => clearTimeout(timeoutId);
    }

    return emptyFunction;
  }, [saveState]);

  const [storeRunScreenerInput, { loading }] = useMutation<
    IStoreRunScreenerInputMutation,
    IStoreRunScreenerInputMutationVariables
  >(StoreRunScreenerInputMutation);

  const generateTitle = () => {
    return intl.formatMessage({ defaultMessage: "Screener | {id}", id: "qe5joO" }, { id: nanoScreenerId() });
  };

  const [title, setTitle] = useState(runScreenerInput?.title || "");
  const promptForTitle = useCallback((defaultValue: string) => {
    const generatedTitle = generateTitle();
    const desiredTitle =
      // eslint-disable-next-line no-alert
      prompt(
        intl.formatMessage({
          defaultMessage: "Digite o nome do seu screener",
          id: "7E+73U",
        }),
        defaultValue || generatedTitle,
      )?.trim() ||
      defaultValue ||
      generatedTitle;
    if (desiredTitle) {
      setTitle(desiredTitle.substring(0, 48));
    }
    return desiredTitle || generatedTitle;
  }, []);

  const amplitude = useAmplitude();

  function runStoreRunScreenerInput({ input, ...rest }: Partial<IStoreRunScreenerInput>) {
    amplitude.logEvent("Saved screener", {
      origin: FundamentalProductOriginCodes.SCREENER,
      vertical: input?.vertical,
      primaryDataSource: input?.primaryDataSource,
      tickerSymbolList: input?.filterByTickerSymbolList || "ALL",
    });
    trackEvent("Saved screener", {
      origin: FundamentalProductOriginCodes.SCREENER,
      vertical: input?.vertical,
      primaryDataSource: input?.primaryDataSource,
      tickerSymbolList: input?.filterByTickerSymbolList || "ALL",
    });

    storeRunScreenerInput({
      variables: {
        input: {
          objectId: objectId || null,
          title,
          description: null,
          input: input!,
          ...rest,
        },
      },
      update: async (cache, { data }) => {
        if (data?.storeRunScreenerInput) {
          const { storeRunScreenerInput } = data;
          cache.writeQuery<IRunScreenerInputQuery, IRunScreenerInputQueryVariables>({
            query: RunScreenerInputQuery,
            variables: {
              language: intl.locale.startsWith("pt") ? "pt-BR" : "en-US",
              objectId: storeRunScreenerInput.objectId,
            },
            data: {
              __typename: "Query",
              runScreenerInput: {
                ...storeRunScreenerInput,
                __typename: "RunScreenerInputContainer",
              },
            },
          });
        }
      },
      onCompleted: async ({ storeRunScreenerInput }) => {
        if (storeRunScreenerInput) {
          onSave?.(storeRunScreenerInput);
          setSaveState("saved");
          setHasPendingChanges(false);
          toast.success(intl.formatMessage({ defaultMessage: "Screener salvo com sucesso", id: "LUdbiA" }));
        }
      },
      onError: () => {
        toast.error(intl.formatMessage({ defaultMessage: "Não foi possível salvar...", id: "M65K8S" }));
        setSaveState("error");
      },
    });
  }

  return (
    <div className="flex items-center gap-x-2">
      <Head>
        <title>{title ? `${title} | Screener - Fundamentei` : `Screener | Fundamentei`}</title>
      </Head>

      <button
        type="button"
        disabled={loading || disabled !== false}
        title={
          hasPendingChanges
            ? intl.formatMessage({ defaultMessage: "Salvar alterações nas configurações", id: "csnMQ7" })
            : intl.formatMessage({ defaultMessage: "Salvar", id: "DFil5M" })
        }
        onClick={() => {
          const lastScreenerInput = RunScreenerInput.getLastSuccessfullyExecutedInput();
          const generatedTitle = generateTitle();
          if (title === "") {
            setTitle(generatedTitle);
          }
          if (lastScreenerInput) {
            runStoreRunScreenerInput({
              objectId: objectId || null,
              input: lastScreenerInput,
              title: title || generatedTitle,
            });
          }
        }}
        className={classNames(
          "relative flex min-h-10 min-w-10 items-center justify-center rounded-xl border border-slate-300 bg-white p-2",
          "hover:bg-slate-100",
          "aria-checked:cursor-pointer aria-checked:bg-slate-200",
          "group",
          "hover:border-blue-400 hover:bg-blue-50",
          "focus:outline-none",
          "disabled:pointer-events-none disabled:opacity-50",
          "dark:border-slate-950 dark:bg-slate-700",
          "dark:hover:bg-lime-400/30",
          {
            "pointer-events-none": saveState === "saved" || saveState === "error",
          },
          {
            "border-orange-400/20 bg-orange-50 dark:bg-yellow-400/40": hasPendingChanges,
          },
        )}
      >
        {saveState === "saved" ? (
          <CircleCheckIcon
            strokeWidth={2}
            absoluteStrokeWidth={true}
            className={classNames(
              "fill-emerald-100 stroke-emerald-700",
              "group-hover:fill-blue-100 group-hover:stroke-blue-600",
              "group-aria-checked:fill-transparent",
              "dark:fill-lime-400/30 dark:stroke-lime-300",
              "size-4",
            )}
          />
        ) : saveState === "error" ? (
          <CircleAlertIcon
            strokeWidth={2}
            absoluteStrokeWidth={true}
            className={classNames(
              "fill-red-100 stroke-red-700",
              "group-hover:fill-blue-100 group-hover:stroke-blue-600",
              "group-aria-checked:fill-transparent",
              "dark:fill-red-400/30 dark:stroke-red-300",
              "size-4",
            )}
          />
        ) : loading ? (
          <LoaderCircleIcon
            strokeWidth={2}
            absoluteStrokeWidth={true}
            className={classNames(
              "stroke-gray-700",
              "group-hover:fill-blue-100 group-hover:stroke-blue-600",
              "group-aria-checked:fill-transparent",
              "size-4",
              "dark:fill-transparent dark:stroke-slate-200",
              "animate-spin",
            )}
          />
        ) : hasPendingChanges ? (
          <BookmarkIcon
            strokeWidth={2.25}
            absoluteStrokeWidth={true}
            className={classNames(
              "stroke-orange-600 group-hover:stroke-blue-700",
              "dark:fill-transparent dark:stroke-yellow-300 dark:group-hover:fill-lime-800 dark:group-hover:stroke-lime-300",
              "size-4",
            )}
          />
        ) : (
          <BookmarkIcon
            strokeWidth={2.25}
            absoluteStrokeWidth={true}
            className={classNames(
              "fill-gray-100 stroke-gray-700 group-hover:fill-blue-100 group-hover:stroke-blue-600",
              "dark:fill-transparent dark:stroke-slate-300 dark:group-hover:fill-lime-800 dark:group-hover:stroke-lime-300",
              "size-4",
            )}
          />
        )}
      </button>
      {title && (
        <div className="flex flex-col text-left">
          <strong title={title} className="max-w-48 truncate text-sm font-bold text-slate-800 dark:text-slate-200">
            {title}
          </strong>
          <div className="inline-flex">
            <button
              type="button"
              className={classNames(
                "text-xs font-bold text-blue-600 dark:text-blue-400",
                "hover:underline hover:underline-offset-2",
              )}
              disabled={loading}
              onClick={() => {
                startTransition(() => {
                  const nextTitle = promptForTitle(title);
                  // Nothing changed?
                  if (nextTitle === title) {
                    return;
                  }
                  const lastScreenerInput = RunScreenerInput.getLastSuccessfullyExecutedInput();
                  if (lastScreenerInput) {
                    runStoreRunScreenerInput({
                      input: lastScreenerInput,
                      title: nextTitle,
                    });
                  }
                });
              }}
            >
              <FormattedMessage defaultMessage="Renomear" id="kV5qpQ" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
